import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { toast } from "sonner";
import blogService from "../../api/blogService";
import SEO from "../../components/SEO/SEO";
import GuidedTour from "../../components/tour/GuidedTour";
import useTour from "../../components/tour/useTour";
import getBlogDetailTourSteps from "./blogDetailTourSteps";
import "./blog-detail.scss";
import { useSelector, useDispatch } from "react-redux";
import usePageTracking from "../../hooks/usePageTracking";
import useBlogEngagementTracking from "../../hooks/useBlogEngagementTracking";
import { trackBlogClick } from "../../utils/blogAnalytics";
import { activityStart, activityEnd } from "../../redux/slices/trackActivitySlice";
import { useTheme } from "../../context/ThemeContext";

const MEDIA_BASE = process.env.REACT_APP_API_BASE_URL || "";

// ── Helpers ──────────────────────────────────────────────────────
function getInitials(name) {
  if (!name) return "??";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatLong(dateString) {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatRelative(dateString) {
  const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)} weeks ago`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)} months ago`;
  return `${Math.floor(diff / 31536000)} years ago`;
}

// ── Add Comment / Reply Form ─────────────────────────────────────
function CommentForm({
  blogSlug,
  isReply = false,
  parentCommentId,
  onSubmitted,
}) {
  const profileStore = useSelector((state) => state.profile);
  const profileData = profileStore?.profileItem?.data;
  const hasInitialized = useRef(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    website: "",
    comment: "",
    notifyFollowUp: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    const profileName = profileData?.full_name || "";
    const profileEmail = profileData?.email || "";
    if (profileName || profileEmail) {
      hasInitialized.current = true;
      setForm((prev) => ({
        ...prev,
        name: profileName,
        email: profileEmail,
      }));
    }
  }, [profileData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);

    const payload = {
      author_name: form.name,
      author_email: form.email,
      content: form.comment,
      notify_on_reply: form.notifyFollowUp,
      ...(form.website ? { author_website: form.website } : {}),
    };

    try {
      let res;
      if (isReply && parentCommentId) {
        res = await blogService.createCommentReply(parentCommentId, payload);
        toast.success(
          res.data?.message ||
            "Reply submitted! It will appear after moderation.",
        );
      } else {
        res = await blogService.createComment(blogSlug, payload);
        toast.success(
          res.data?.message ||
            "Comment submitted! It will appear after moderation.",
        );
      }
      setForm((prev) => ({ ...prev, comment: "", notifyFollowUp: false }));
      if (onSubmitted) onSubmitted();
    } catch (err) {
      const errData = err.response?.data;
      toast.error(
        errData?.data?.message ||
          errData?.message ||
          "Failed to submit. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="blog-add-comment"
      data-tour={!isReply ? "bd-comment-form" : undefined}
    >
      {!isReply && (
        <>
          <h3 className="blog-add-comment-title">Leave a Comment</h3>
          <p className="blog-add-comment-hint">
            Your email address will not be published. Required fields are marked
            *
          </p>
        </>
      )}
      <form className="blog-comment-form" onSubmit={handleSubmit}>
        <div className="blog-form-row">
          <div className="blog-form-group">
            <label htmlFor={`${isReply ? "r" : "c"}-name`}>
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id={`${isReply ? "r" : "c"}-name`}
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              placeholder="Your name"
            />
          </div>
          <div className="blog-form-group">
            <label htmlFor={`${isReply ? "r" : "c"}-email`}>
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id={`${isReply ? "r" : "c"}-email`}
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
            />
          </div>
          <div className="blog-form-group">
            <label htmlFor={`${isReply ? "r" : "c"}-website`}>Website</label>
            <input
              type="url"
              id={`${isReply ? "r" : "c"}-website`}
              name="website"
              value={form.website}
              onChange={handleChange}
              placeholder="https://yourwebsite.com"
            />
          </div>
        </div>

        <div className="blog-form-group">
          <label htmlFor={`${isReply ? "r" : "c"}-comment`}>
            {isReply ? "Reply" : "Comment"} <span className="required">*</span>
          </label>
          <textarea
            id={`${isReply ? "r" : "c"}-comment`}
            name="comment"
            value={form.comment}
            onChange={handleChange}
            required
            rows={5}
            placeholder={
              isReply ? "Write your reply..." : "Share your thoughts..."
            }
          />
        </div>

        <div className="blog-form-checks">
          <label className="blog-form-check">
            <input
              type="checkbox"
              name="notifyFollowUp"
              checked={form.notifyFollowUp}
              onChange={handleChange}
            />
            <span>Notify me of follow-up comments by email.</span>
          </label>
        </div>

        <div>
          <button type="submit" className="blog-submit-btn" disabled={busy}>
            {busy ? "Submitting..." : isReply ? "Post Reply" : "Post Comment"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Comments List ────────────────────────────────────────────────
function CommentsList({ slug }) {
  const [page, setPage] = useState(1);
  const [allComments, setAllComments] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalComments, setTotalComments] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [expandedThreads, setExpandedThreads] = useState(new Set());
  const loaderRef = useRef(null);
  const isFetchingRef = useRef(false);
  const fetchComments = useCallback(
    async (pageNum) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setIsLoading(true);
      try {
        const res = await blogService.getComments(slug, {
          page: pageNum,
          limit: 5,
        });
        const data = res.data?.data;
        if (data) {
          const docs = data.docs || [];
          const pages = data.pages || 1;
          setTotalComments(data.total || 0);
          setHasMore(pageNum < pages);
          setAllComments((prev) => {
            const existingIds = new Set(prev.map((c) => c.id));
            const fresh = docs.filter((c) => !existingIds.has(c.id));
            return [...prev, ...fresh];
          });
        }
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    },
    [slug],
  );

  useEffect(() => {
    setPage(1);
    setAllComments([]);
    setHasMore(true);
    setError(null);
  }, [slug]);

  useEffect(() => {
    fetchComments(page);
  }, [page, fetchComments]);

  const handleObserver = useCallback(
    (entries) => {
      if (entries[0].isIntersecting && hasMore && !isLoading) {
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, isLoading],
  );

  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: "100px",
      threshold: 0.1,
    });
    observer.observe(el);
    return () => observer.unobserve(el);
  }, [handleObserver]);

  const refresh = () => {
    setPage(1);
    setAllComments([]);
    setHasMore(true);
    setReplyingTo(null);
  };

  const toggleThread = (id) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const renderReply = (reply) => (
    <div key={reply.id}>
      <div className="blog-reply-item">
        <div className="blog-comment-row">
          <div className="blog-comment-avatar reply-avatar">
            {getInitials(reply.author_name)}
          </div>
          <div className="blog-comment-body">
            <div className="blog-comment-header">
              <p className="blog-comment-author">{reply.author_name}</p>
              <span className="blog-comment-time">
                {formatRelative(reply.created_at)}
              </span>
            </div>
            <p className="blog-comment-text">{reply.content}</p>
            <button
              className="blog-comment-reply-btn"
              onClick={() =>
                setReplyingTo(replyingTo === reply.id ? null : reply.id)
              }
            >
              {replyingTo === reply.id ? "Cancel" : "Reply"}
            </button>
            {replyingTo === reply.id && (
              <div className="blog-inline-reply">
                <p className="blog-inline-reply-label">
                  Replying to <strong>{reply.author_name}</strong>
                </p>
                <CommentForm
                  blogSlug={slug}
                  isReply
                  parentCommentId={reply.id}
                  onSubmitted={refresh}
                />
              </div>
            )}
          </div>
        </div>
        {reply.replies?.length > 0 && reply.replies.map((r) => renderReply(r))}
      </div>
    </div>
  );

  const renderComment = (comment) => {
    const hasReplies = comment.replies?.length > 0;
    const isExpanded = expandedThreads.has(comment.id);
    const replyCount = comment.replies?.length || 0;

    return (
      <div key={comment.id} className="blog-comment-item">
        <div className="blog-comment-row">
          <div className="blog-comment-avatar">
            {getInitials(comment.author_name)}
          </div>
          <div className="blog-comment-body">
            <div className="blog-comment-header">
              <p className="blog-comment-author">{comment.author_name}</p>
              <span className="blog-comment-time">
                {formatRelative(comment.created_at)}
              </span>
            </div>
            <p className="blog-comment-text">{comment.content}</p>
            <button
              className="blog-comment-reply-btn"
              onClick={() =>
                setReplyingTo(replyingTo === comment.id ? null : comment.id)
              }
            >
              {replyingTo === comment.id ? "Cancel" : "Reply"}
            </button>

            {replyingTo === comment.id && (
              <div className="blog-inline-reply">
                <p className="blog-inline-reply-label">
                  Replying to <strong>{comment.author_name}</strong>
                </p>
                <CommentForm
                  blogSlug={slug}
                  isReply
                  parentCommentId={comment.id}
                  onSubmitted={refresh}
                />
              </div>
            )}

            {hasReplies && (
              <div>
                <button
                  className="blog-replies-toggle"
                  onClick={() => toggleThread(comment.id)}
                >
                  <svg
                    className={isExpanded ? "rotated" : ""}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                  {isExpanded ? "Hide" : "Show"} {replyCount}{" "}
                  {replyCount === 1 ? "reply" : "replies"}
                </button>
                {isExpanded && (
                  <div className="blog-replies-thread">
                    {comment.replies.map((r) => renderReply(r))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading && page === 1 && allComments.length === 0) {
    return (
      <div className="blog-comments-section">
        <h3>Comments</h3>
        <p className="blog-comments-loading">Loading comments...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="blog-comments-section">
        <h3>Comments</h3>
        <p className="blog-comments-error">Failed to load comments.</p>
      </div>
    );
  }
  if (allComments.length === 0 && !isLoading) {
    return (
      <div className="blog-comments-section">
        <h3>Comments (0)</h3>
        <p className="blog-comments-empty">
          No comments yet. Be the first to comment!
        </p>
      </div>
    );
  }

  return (
    <div className="blog-comments-section">
      <h3>Comments ({totalComments})</h3>
      <div className="blog-comments-list">
        {allComments.map((c) => renderComment(c))}
      </div>
      <div ref={loaderRef} className="blog-comments-loader">
        {isLoading && page > 1 && <span className="blog-mini-spinner" />}
        {!hasMore && allComments.length > 5 && (
          <p className="blog-comments-no-more">No more comments</p>
        )}
        {!isLoading && hasMore && (
          <p className="blog-comments-scroll-hint">Scroll to load more</p>
        )}
      </div>
    </div>
  );
}

// ── Main Blog Detail Page ─────────────────────────────────────────
export default function BlogDetail() {
  const { slug } = useParams();
  const dispatch = useDispatch();
  usePageTracking("BLOG_DETAIL");
  useEffect(() => {
    dispatch(activityStart({ page_name: "BLOG_DETAIL", platform: "web" }));
    return () => dispatch(activityEnd({ page_name: "BLOG_DETAIL" }));
  }, [dispatch]);
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();

  const [post, setPost] = useState(null);
  const [categories, setCategories] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgError, setImgError] = useState(false);

  // Process HTML before render: wrap tables and prevent cell text wrapping.
  // DOMParser runs synchronously so there are no timing/ref issues.
  const processedContent = useMemo(() => {
    if (!post?.content) return "";
    const doc = new DOMParser().parseFromString(post.content, "text/html");

    doc.querySelectorAll("table").forEach((table) => {
      // Scroll wrapper
      const wrapper = doc.createElement("div");
      wrapper.style.cssText =
        "width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;margin:1rem 0;display:block;";

      // Table itself — natural width, no squishing
      table.style.cssText =
        "width:max-content;min-width:100%;border-collapse:collapse;font-size:0.9rem;";

      // Every cell: prevent text from wrapping (this is what forces the table wider than the screen)
      table.querySelectorAll("th, td").forEach((cell) => {
        cell.style.whiteSpace = "nowrap";
        cell.style.padding = "8px 14px";
      });

      // The CMS bakes light pastel row-highlight colors directly as inline
      // styles (e.g. style="background: #e8f5e9") which never adapt to dark
      // mode and swallow our theme-reactive text color. Neutralize any
      // inline background on rows/cells here instead of fighting it in CSS,
      // since inline styles win the cascade regardless of selector.
      if (resolvedTheme === "dark") {
        table.querySelectorAll("tr, td, th").forEach((el) => {
          if (el.style.background || el.style.backgroundColor) {
            el.style.background = "";
            el.style.backgroundColor = "var(--mmp-surface)";
          }
        });
      }

      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });

    return doc.body.innerHTML;
  }, [post, resolvedTheme]);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setPost(null);
    setImgError(false);
    window.scrollTo(0, 0);

    Promise.all([
      blogService.getBlogDetail(slug),
      blogService.getCategories(),
      blogService.getBlogs({ page: 1, limit: 6, sort: "latest" }),
    ])
      .then(([postRes, catsRes, recentRes]) => {
        setPost(postRes.data?.data);
        setCategories(catsRes.data?.data || []);
        setRecentPosts(recentRes.data?.data?.docs || []);
      })
      .catch((err) => setError(err))
      .finally(() => setIsLoading(false));
  }, [slug]);

  // Guided tour — auto-starts once the post has loaded (so the ring has real
  // content to anchor to), replayable from the header's tour button
  // (pageKey "blog-detail"). Declared before the early returns below to keep
  // hook order stable.
  const { isOpen: isTourOpen, finish: finishTour } = useTour("blog-detail", {
    autoStart: !!post,
  });

  // Records a view on load and flushes reading-time/scroll-depth when the reader leaves.
  useBlogEngagementTracking(post && !isLoading && !error ? slug : undefined);

  if (isLoading) {
    return (
      <div className="blog-detail-page">
        <div className="blog-detail-container">
          <div className="blog-detail-center">
            <div className="blog-detail-spinner" />
            <p>Loading blog post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-detail-page">
        <div className="blog-detail-container">
          <div className="blog-detail-center">
            <h2 style={{ color: "var(--mmp-text-primary)", marginBottom: "0.5rem" }}>
              Post not found
            </h2>
            <a
              className="blog-detail-error-link"
              href="/blog"
              onClick={(e) => {
                e.preventDefault();
                navigate("/blog");
              }}
            >
              ← Back to Blog
            </a>
          </div>
        </div>
      </div>
    );
  }

  const imageUrl = post.featured_image
    ? `${MEDIA_BASE}/uploads/blog/${post.featured_image}`
    : null;

  return (
    <>
    <SEO
      title={`${post.title} | Map My Pay Blog`}
      description={
        post.excerpt ||
        (post.content
          ? post.content.replace(/<[^>]*>/g, "").substring(0, 160) + "..."
          : undefined)
      }
      url={`${window.location.origin}/blog/${slug}`}
      type="article"
      image={imageUrl || undefined}
    />
    <GuidedTour
      isOpen={isTourOpen}
      steps={getBlogDetailTourSteps()}
      onDone={finishTour}
    />
    <div className="blog-detail-page">
      <div className="blog-detail-container">
        {/* Back link */}
        <div className="app-page-title">
          <div className="page-title-heading d-flex align-items-center">
            <FiArrowLeft
              size={40}
              className="icon cursor-pointer"
              onClick={() => navigate("/blog", { state: { fromBlogDetail: true } })}
            />
            <h3 className="page-title">Blog Detail</h3>
          </div>
        </div>

        <div className="blog-detail-grid">
          {/* ── Main content ──────────────────────────────── */}
          <div className="blog-detail-main">
            {imageUrl && !imgError && (
              <div className="blog-detail-hero">
                <img
                  src={imageUrl}
                  alt={post.title}
                  onError={() => setImgError(true)}
                />
              </div>
            )}

            <div className="blog-detail-meta" data-tour="bd-article">
              <p className="blog-detail-date">
                {formatLong(post.created_at)}
                {post.reading_time_minutes ? ` · ${post.reading_time_minutes} min read` : ""}
              </p>
              <h1>{post.title}</h1>
              {post.excerpt && (
                <p className="blog-detail-excerpt">{post.excerpt}</p>
              )}
              {post.categories?.length > 0 && (
                <div className="blog-detail-cats">
                  {post.categories.map((cat) => (
                    <span key={cat.id} className="blog-detail-cat-badge">
                      {cat.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div
              className="blog-detail-content"
              dangerouslySetInnerHTML={{ __html: processedContent }}
              onClick={(e) => {
                const link = e.target.closest("a");
                if (!link) return;
                const href = link.getAttribute("href") || "";
                const isExternal = /^https?:\/\//i.test(href) && !href.includes(window.location.hostname);
                trackBlogClick(slug, isExternal ? `content_link_external:${href.slice(0, 80)}` : "content_link_internal");
              }}
            />

            <CommentsList slug={slug} key={slug} />

            <CommentForm blogSlug={slug} />
          </div>

          {/* ── Sidebar ───────────────────────────────────── */}
          <div className="blog-detail-sidebar">
            {categories.length > 0 && (
              <div className="blog-sidebar-section" data-tour="bd-categories">
                <h3>Category</h3>
                <div className="blog-sidebar-cats">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={`/blog?category_id=${cat.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/blog?category_id=${cat.id}`);
                      }}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {recentPosts.length > 0 && (
              <div className="blog-sidebar-section" data-tour="bd-recent">
                <h3>Recent Posts</h3>
                <div className="blog-sidebar-recent">
                  {recentPosts.map((rp) => (
                    <a
                      key={rp.id}
                      className="blog-sidebar-post"
                      href={`/blog/${rp.slug}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/blog/${rp.slug}`);
                      }}
                    >
                      <span className="blog-sidebar-post-date">
                        {formatLong(rp.created_at)}
                      </span>
                      <span className="blog-sidebar-post-title">
                        {rp.title}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
